import { t } from 'i18next';
import moment from 'moment';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { setMessage } from '../../store/reducers/appSlice';
import store from '../../store/store';
import generateErrorMessage from '../helper-functions/generateErrorMessage';
import { tokenRevokeAtLogOutUser } from './logout-user';
import { submitLogToBugsnag } from './log';
import makeFirstLetterCapital from '../helper-functions/makeFirstLetterCapital';
import { APP_ENV, INTERCOM_ENABLED_ENVS } from '../constants/central-api';
import localStorageHardReset from '../helper-functions/localStorageHardReset';
import { setAiTrackerAccessToken } from '../../store/reducers/userSlice';
import { isNoActivePlanResponse } from '../plan-lock/plan-lock-rules';
import { currentSignIn, reportNoActivePlan } from '../plan-lock/plan-lock';

/**
 * True when this browser session authenticates as an agency EMPLOYEE only —
 * an `emp_` key in Redux, a resolved employee identity, and no seller JWT.
 * These are the invite-only employees created from the Team page; they never
 * receive a seller token because they have no `users`/seller row.
 */
function isAgencyOnlySession(): boolean {
  const state = store.getState();
  const accessToken = state.auth.authorization.accessToken;
  const sellerToken = state.UI?.isWhiteLabel
    ? accessToken?.currentWhiteLabelSellerToken
    : accessToken?.sellerToken;

  if (sellerToken) {
    return false;
  }

  // Deliberately keyed on the KEY alone, not on `employeeIdentity`: on a cold
  // boot the key is applied from `/session` before `/me` resolves the identity,
  // and a seller-surface 401 landing in that window must not wipe the session
  // either.
  return !!state.auth.agency?.accessToken;
}

/**
 * True when the request that just 401'd was actually sent with the agency
 * `emp_` key. Only then does the 401 mean "this agency session is dead"; a 401
 * from a request carrying a seller JWT (or no credential at all) says nothing
 * about the validity of the employee's key.
 */
function usedAgencyCredential(requestInfo: RequestInfo): boolean {
  const agencyKey = store.getState().auth.agency?.accessToken;

  if (!agencyKey) {
    return false;
  }

  const headers = (requestInfo.headers ?? {}) as Record<string, string>;
  const authorization = headers.Authorization ?? headers.authorization ?? '';

  return authorization.includes(agencyKey);
}

/** True when `Authorization` carries an actual token (not `Bearer `/`undefined`). */
function carriesCredential(requestInfo: RequestInfo): boolean {
  const headers = (requestInfo.headers ?? {}) as Record<string, string>;
  const authorization = String(
    headers.Authorization ?? headers.authorization ?? ''
  );
  const token = authorization.replace(/^\s*Bearer\s*/i, '').trim();
  return token !== '' && token !== 'undefined' && token !== 'null';
}

/** True when this browser holds a seller or agency credential right now. */
function holdsSessionCredential(): boolean {
  const { authorization, agency } = store.getState().auth;
  return [
    authorization?.accessToken?.sellerToken,
    authorization?.accessToken?.currentWhiteLabelSellerToken,
    agency?.accessToken,
  ].some((value) => typeof value === 'string' && value !== '');
}

export const asyncHandler = async (
  requestInfo: RequestInfo,
  actionType: string,
  customMsg = t('Success'),
  shouldShowMsg = true,
  ignore401ForceLogOut = false,
  shouldShowErrorMsg = false,
  isTrackerAPI = false,
  blackListenMsgs: string[] = [],
  forceHideMsg = false
) => {
  const accessToken = store.getState().auth.authorization.accessToken;
  // method,headers,body,token,url
  if (!requestInfo.method || !actionType) {
    console.log('Required parameters are missing!');
    return null;
  }

  if (!requestInfo.headers) {
    requestInfo.headers = {};
  }

  /* --------------Token expiry check Start---------------- */
  if (accessToken?.expiresOn && accessToken?.expiresOn !== null) {
    if (moment(accessToken?.expiresOn) < moment()) {
      logoutHandler();
      return null;
    }
  }
  /* --------------Token expiry check End---------------- */

  // Taken before the request: a 401 for a browser that was not signed in (a
  // signed-out visitor, a request sent without a token) is not an expired
  // session and must not say so.
  const hadSession = holdsSessionCredential() || carriesCredential(requestInfo);

  try {
    const sentAs = currentSignIn();
    const response = await fetch(requestInfo.url, {
      method: requestInfo.method,
      headers: requestInfo.headers,
      body: requestInfo.body || null,
      signal: requestInfo.signal, // NEW: attach the AbortController's signal if provided
      // Phase B — only set when the caller opts in (central-api login/logout),
      // so the browser stores/sends the httpOnly agency-session cookie. Omitted
      // (undefined) for all other calls, preserving fetch's default behaviour.
      credentials: requestInfo.credentials,
    });

    const triggerUnauthorizedAccessToast = () => {
      store.dispatch(
        setMessage({
          message: t('UnauthorizedAccess'),
          messageType: 'error',
        })
      );
    };

    const responseStatus = response?.status;

    if (responseStatus === 401 && !ignore401ForceLogOut) {
      const isWhiteLabel = store.getState().UI.isWhiteLabel;

      // An invite-only agency employee holds an `emp_` key and NO seller JWT
      // (`agency/employees/login` returns `access_token: null` by design). A
      // number of legacy endpoints are still seller-JWT-only, so they answer
      // 401 for a session that is perfectly valid — wiping it here logged the
      // employee out of the whole portal and surfaced "Token has expired.
      // Please login again.". Only the AGENCY credential failing may end an
      // agency-only session; a seller-surface 401 must not.
      //
      // Same class of bug as the forced LOGOUT `fetchAppInit` used to perform
      // for these sessions (fixed in the employee-brand-visibility hotfix).
      if (isAgencyOnlySession() && !usedAgencyCredential(requestInfo)) {
        submitLogToBugsnag(
          'info',
          `401 on a seller-only endpoint for an agency-only session; session preserved: ${requestInfo.url}`
        );
      } else {
        localStorageHardReset();

        if (isWhiteLabel) {
          window.location.assign('/authorize');
        } else if (hadSession) {
          triggerUnauthorizedAccessToast();
        }
      }
    }

    /* ==============================Tracker API ERROR Handling========================== */
    if (isTrackerAPI) {
      if (responseStatus === 401 || responseStatus === 503) {
        console.log('❌ Tracker API Unauthorized or Service Unavailable');
        store.dispatch(setAiTrackerAccessToken(''));
      }
    }
    /* ==============================Tracker API ERROR Handling========================== */

    if (responseStatus === 204) {
      return { data: null, actionType, responseStatus };
    }

    const data = await response.json();
    console.log(actionType);
    console.log(data);

    // The organisation has no active plan or trial: central-api refuses every
    // gated call with the same 402. Lock the app once and stay quiet; a toast
    // per refused call would bury the screen that explains it.
    if (isNoActivePlanResponse(responseStatus, data)) {
      reportNoActivePlan(data, sentAs);
      return null;
    }

    if (data) {
      let message, messageType;
      if (data.success === true && data.messages?.length > 0) {
        message = data.messages;
        messageType = 'success';
      } else if (
        data.success === true &&
        Object.values(data.messages ? data.messages : {}).length > 0
      ) {
        message = Object.values(data.messages).join(`, `);
        messageType = 'success';
      } else if (data.success === true) {
        message = customMsg;
        messageType = 'success';
      } else if (data.success === false && responseStatus === 422) {
        console.log(generateErrorMessage(data));
        message = generateErrorMessage(data);
        messageType = 'warning';
        shouldShowMsg = true;
        submitLogToBugsnag('error', generateErrorMessage(data));
      } else if (data.success === false) {
        console.log(generateErrorMessage(data));
        message = generateErrorMessage(data);
        messageType = 'error';
        if (shouldShowErrorMsg) {
          shouldShowMsg = true;
        }
        submitLogToBugsnag('error', generateErrorMessage(data));
      } else if (data.message === 'Unauthenticated.') {
        logoutHandler();
        return null;
      } else {
        const errorActionType = makeFirstLetterCapital(
          actionType?.replaceAll('_', ' ')
        );
        submitLogToBugsnag(
          'error',
          `Something went wrong in ${errorActionType} request.`
        );
        throw new Error('Something went wrong');
      }

      if (
        shouldShowMsg &&
        !blackListenMsgs.includes(message) &&
        !forceHideMsg
      ) {
        store.dispatch(setMessage({ message, messageType }));
      }

      return { data, actionType, responseStatus };
    }
  } catch (err: any) {
    console.log(err.message);
    // NEW: Check if the error indicates that the request was aborted.
    if (
      err.name === 'AbortError' ||
      (err.message && err.message.toLowerCase().includes('aborted'))
    ) {
      console.log('Request was aborted.');
      return { aborted: true };
    }
    submitLogToBugsnag('error', ` ${err?.message ?? err}`);
    if (
      shouldShowMsg &&
      !blackListenMsgs.includes(err?.message) &&
      !forceHideMsg
    ) {
      store.dispatch(
        setMessage({ message: err.message, messageType: 'error' })
      );
    }
    return null;
  }
};

const logoutHandler = () => {
  console.log('❌ Access token expired. Logging Out user');
  store.dispatch(
    setMessage({
      message: t('SessionExpired'),
      messageType: 'error',
    })
  );
  tokenRevokeAtLogOutUser();
  if (INTERCOM_ENABLED_ENVS.includes(APP_ENV)) {
    // @ts-ignore:next-line
    window.Intercom && window.Intercom('shutdown'); //shutdown intercom
  }

  localStorageHardReset();
};
